import React, { useState } from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';

import { sendAnalytics } from 'components/helpers/analytics';

export default function ELearningLauncher({
  booking,
  bookingHashid,
  onError,
  onRegistrationUpdate,
  registrationUuid,
}) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [launchLink, setLaunchLink] = useState(null);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'eLearning.ELearningLauncher',
  });

  const { t: i18nTCourses } = useTranslation('common', {
    keyPrefix: 'courses',
  });

  const personnelId = booking.data.relationships.personnel.data.id;
  const courseId = booking.data.relationships.course.data.id;

  const createRegistrationThenLaunchELearning = () => {
    axios
      .post('/e_learning/registrations', {
        registration: { booking_id: bookingHashid },
      })
      .then((response) => {
        const newRegistrationUuid = response.data.data.attributes.uuid;
        onRegistrationUpdate({ uuid: newRegistrationUuid });
        launchELearning(newRegistrationUuid);
      })
      .catch(onError);
  };

  const launchELearning = (launchRegistrationUuid) => {
    axios
      .post(`/e_learning/registrations/${launchRegistrationUuid}/launch_links`)
      .then((response) => {
        sendAnalytics('eLearning course started', {
          personnelId,
          courseId,
          registrationUuid: launchRegistrationUuid,
        });
        setLaunchLink(response.data.data.attributes.launchLink);
      })
      .catch(onError);
  };

  const syncRegistration = () => {
    axios
      .get(`/e_learning/registrations/${registrationUuid}?sync=true`)
      .then((response) => {
        if (response.data.meta.isSynced) {
          const {
            completedAndPassed,
            completedAndPassedWithTest,
            completionStatus,
            score,
            successStatus,
          } = response.data.data.attributes;

          sendAnalytics('eLearning course synced', {
            personnelId,
            courseId,
            registrationUuid,
            completionStatus,
            successStatus,
            score,
          });
          onRegistrationUpdate({
            completionStatus,
            successStatus,
            score,
            completedAndPassed,
            completedAndPassedWithTest,
          });
        }

        setIsButtonDisabled(false);
      })
      .catch(onError);
  };

  const handleButtonClick = () => {
    setIsButtonDisabled(true);
    registrationUuid ?
      launchELearning(registrationUuid)
    : createRegistrationThenLaunchELearning();
  };

  const handleIFrameLoad = (event) => {
    try {
      if (event.target.contentWindow.location.pathname === '/200.html') {
        syncRegistration();
      }
    } catch {
      console.warn('unable to sync registration');
    }
  };

  return (
    <>
      <FilledButton
        color='mint'
        disabled={isButtonDisabled}
        onClick={handleButtonClick}
      >
        {registrationUuid ?
          i18nTCourses('continueCourse')
        : i18nT('button.takeCourse')}
      </FilledButton>
      <iframe
        className='e-learning__iframe'
        onLoad={handleIFrameLoad}
        src={launchLink}
        title={i18nT('iframe.title')}
      />
    </>
  );
}

ELearningLauncher.propTypes = {
  bookingHashid: PropTypes.string.isRequired,
  booking: PropTypes.shape({
    data: PropTypes.shape({
      relationships: PropTypes.shape({
        personnel: PropTypes.object.isRequired,
        course: PropTypes.object.isRequired,
      }),
    }),
  }),
  registrationUuid: PropTypes.string,
  onRegistrationUpdate: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

ELearningLauncher.defaultProps = {
  registrationUuid: null,
};
